import { useMemo } from "react";
import emberleeData from "../characters/emberlee.json";
import {
  PersonaImageCollection,
  type PersonaImageInfo,
} from "@um/contracts";
import type { Persona } from "@um/contracts";
import type { AnimationFrame } from "./useFrameAnimation";

const emberleePersona = emberleeData.supportAnimals[0]
  .personas[0] as unknown as Persona;

function buildCollection() {
  return new PersonaImageCollection(emberleePersona);
}

let _cached: PersonaImageCollection | null = null;
function getCachedCollection(): PersonaImageCollection {
  if (!_cached) _cached = buildCollection();
  return _cached;
}

type EmberleePoseContext =
  | "greeting"
  | "thinking"
  | "excited"
  | "listening"
  | "pointing"
  | "lookingDown"
  | "idle";

/**
 * Provides access to Emberlee's expression sheet panels and pre-built
 * animation sequences. Uses the bundled emberlee.json data.
 */
export function useEmberlee() {
  const collection = useMemo(() => getCachedCollection(), []);

  const lookup = useMemo(
    () => (panelId: string) => collection.lookup(panelId),
    [collection]
  );

  const getAnimation = useMemo(() => {
    const l = (id: string): PersonaImageInfo => collection.lookup(id)!;

    const animations: Record<
      EmberleePoseContext,
      AnimationFrame<PersonaImageInfo>[]
    > = {
      greeting: [
        { data: l("A1"), durationMs: 600 },
        { data: l("A2"), durationMs: 400 },
        { data: l("A1"), durationMs: 300 },
        { data: l("A14"), durationMs: 2000 },
      ],
      thinking: [
        { data: l("A7"), durationMs: 2000 },
        { data: l("A8"), durationMs: 1500 },
        { data: l("A7"), durationMs: 2000 },
      ],
      excited: [
        { data: l("B1"), durationMs: 1800 },
        { data: l("B2"), durationMs: 1800 },
        { data: l("B3"), durationMs: 1800 },
        { data: l("B4"), durationMs: 1800 },
        { data: l("B5"), durationMs: 1800 },
        { data: l("B6"), durationMs: 1800 },
        { data: l("B7"), durationMs: 1800 },
        { data: l("B8"), durationMs: 1800 },
        { data: l("B16"), durationMs: 3000 },
      ],
      listening: [
        { data: l("A5"), durationMs: 2500 },
        { data: l("A8"), durationMs: 2000 },
      ],
      pointing: [{ data: l("A4"), durationMs: 2500 }],
      lookingDown: [{ data: l("A3"), durationMs: 5000 }],
      idle: [
        { data: l("A8"), durationMs: 3000 },
        { data: l("A12"), durationMs: 2000 },
      ],
    };

    return (context: EmberleePoseContext) => animations[context];
  }, [collection]);

  return {
    collection,
    lookup,
    getAnimation,
    persona: emberleePersona,
  };
}

export type { EmberleePoseContext };
